<script>
import constants from '@/constants';

export default {
    name: 'MaterialRequestsAdminFilters',

    data() {
        return {
            // Local copy for debounced publisher search
            localPublisherSearch: '',
            searchDebounceTimer: null,
        };
    },

    computed: {
        filters() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS_FILTERS;
        },

        statusOptions() {
            return [
                { label: 'All Statuses', value: null },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.new.text,
                    value: constants.MATERIAL_REQUEST_STATUS.NEW,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.in_progress
                        .text,
                    value: constants.MATERIAL_REQUEST_STATUS.IN_PROGRESS,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.done.text,
                    value: constants.MATERIAL_REQUEST_STATUS.DONE,
                },
                {
                    label: constants.MATERIAL_REQUEST_STATUS_CONFIG.rejected
                        .text,
                    value: constants.MATERIAL_REQUEST_STATUS.REJECTED,
                },
            ];
        },
    },

    methods: {
        // ── Status filter ────────────────────────────────────────────────────
        handleStatusChange(event) {
            const value = event.target.value === '' ? null : event.target.value;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestsAdminFilters.handleStatusChange] status →',
                    value
                );
            }
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_FILTERS', {
                status: value,
            });
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', 1);
            this.$store.dispatch('loadAdminMaterialRequests');
        },

        // ── Publisher search ─────────────────────────────────────────────────
        handlePublisherInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.applyPublisherFilter();
            }, 300);
        },

        applyPublisherFilter() {
            const val = this.localPublisherSearch.trim() || null;
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestsAdminFilters.applyPublisherFilter] publisher_id filter applied'
                );
            }
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_FILTERS', {
                publisher_id: val,
            });
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', 1);
            this.$store.dispatch('loadAdminMaterialRequests');
        },

        clearPublisherSearch() {
            this.localPublisherSearch = '';
            this.applyPublisherFilter();
        },

        // ── Clear all filters ────────────────────────────────────────────────
        clearAllFilters() {
            this.localPublisherSearch = '';
            clearTimeout(this.searchDebounceTimer);
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_FILTERS', {
                status: null,
                publisher_id: null,
            });
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', 1);
            this.$store.dispatch('loadAdminMaterialRequests');
        },
    },
};
</script>

<template>
    <div class="filters_container">
        <!-- Status dropdown -->
        <div class="filter_group">
            <label class="filter_label">Status</label>
            <select
                :value="filters.status || ''"
                @change="handleStatusChange"
                class="filter_select"
            >
                <option
                    v-for="opt in statusOptions"
                    :key="String(opt.value)"
                    :value="opt.value === null ? '' : opt.value"
                >
                    {{ opt.label }}
                </option>
            </select>
        </div>

        <!-- Publisher search -->
        <div class="filter_group">
            <label class="filter_label">Publisher</label>
            <div class="search_box">
                <input
                    v-model="localPublisherSearch"
                    @input="handlePublisherInput"
                    type="text"
                    placeholder="Search by publisher ID or name..."
                    class="filter_input"
                />
                <button
                    v-if="localPublisherSearch"
                    @click="clearPublisherSearch"
                    class="clear_btn"
                    title="Clear"
                >
                    ✕
                </button>
            </div>
        </div>

        <!-- Clear all -->
        <div class="filter_group filter_group_actions">
            <button
                v-if="filters.status || filters.publisher_id"
                @click="clearAllFilters"
                class="btn_clear_all"
            >
                Clear All
            </button>
        </div>
    </div>
</template>

<style scoped>
.filters_container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 16px;
}

.filter_group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
}

.filter_group_actions {
    flex-direction: row;
    align-items: flex-end;
    padding-bottom: 2px;
}

.filter_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.filter_select {
    padding: 8px 10px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    cursor: pointer;
    min-width: 160px;
}

.filter_select:focus {
    outline: none;
    border-color: var(--primary-color);
}

.search_box {
    position: relative;
    display: flex;
    align-items: center;
}

.filter_input {
    padding: 8px 36px 8px 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-size: 14px;
    width: 220px;
    transition: border-color 0.2s ease;
}

.filter_input:focus {
    outline: none;
    border-color: var(--primary-color);
}

.clear_btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    font-size: 14px;
    padding: 4px;
    line-height: 1;
}

.clear_btn:hover {
    color: var(--text-color);
}

.btn_clear_all {
    padding: 8px 14px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: transparent;
    color: var(--text-color-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn_clear_all:hover {
    border-color: var(--text-color);
    color: var(--text-color);
}
</style>
